import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {createGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import {Operators} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type LabelPrimitivesOperatorParameters<TAttribute> = {
    name: string
    attribute: TAttribute | null
}

export const labelPrimitivesOperatorParametersSchema = <TAttribute>(attributeSchema: z.ZodMiniType<TAttribute, TAttribute>) =>
    z.object({
        name: z.string(),
        attribute: z.nullable(attributeSchema),
    })

function applyLabelPrimitivesOperator<T extends IndexingDim>(attribute: GeometryAttributeData<T>): GeometryAttributeData<T> {
    const attrRefData = attribute.ref
    const labeledPrimitivesAttrRefData = applyGeometryOperator(Operators.labelPrimitives, attrRefData)
    return createGeometryAttributeData(labeledPrimitivesAttrRefData, attribute.dim)
}

export abstract class GeometryLabelPrimitivesOperatorNode<
    out T extends IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
> extends GeometryAttributeNode<T, LabelPrimitivesOperatorParameters<TAttribute>> {
    protected override hasValidParameters(
        parameters: LabelPrimitivesOperatorParameters<TAttribute>,
    ): parameters is LabelPrimitivesOperatorParameters<TAttribute> & NonNullableParameters<LabelPrimitivesOperatorParameters<TAttribute>> {
        return parameters.attribute !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<LabelPrimitivesOperatorParameters<TAttribute>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<T> | null>; invalidator?: BuilderInlet<null>} {
        const {attribute} = parameters

        const [evaluatedAttributeDataValid, evaluatedAttributeDataInvalid] = scope.branch(evaluator.evaluate(attribute))

        const geometryAttributeData = scope.pureLambda(
            evaluatedAttributeDataValid,
            (attributeData) => applyLabelPrimitivesOperator(attributeData),
            "applyLabelPrimitivesOperator",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: evaluatedAttributeDataInvalid}
    }
}
